import React from 'react';
import { filter, map, pipe, find, isNil, partition, sort, ascend, prop, addIndex, concat } from 'ramda';
import { IQuestionWithOwner } from '../../../../common/models/question/IQuestionWithOwner';
import { ReferenceType } from '../../../../common/enums/ReferenceType';
import { QuestionType } from '../../../../common/enums/QuestionType';
import { ISurveyQuestion } from '../../../../common/models/survey/ISurveyQuestion';
import { IPreviewQuestion } from '../../../../common/models/question/IPreviewQuestion';
import Icon from '../../../../components/Icon';
import { IFormattedMessage } from '../../../../common/models/common/IFormattedMessage';
import { QuestionSection } from '../../../../common/enums/QuestionSection';

export const sendToAll = [ReferenceType.Manager,
  ReferenceType.Peer,
  ReferenceType.Subordinate,
  ReferenceType.Educator,
  ReferenceType.Other];

export const requiredQuestion: IQuestionWithOwner = {
  id: 'required',
  text: 'Identity / Employment Verification',
  type: QuestionType.Verification,
  isRequired: true,
  options: [],
  defaultSendTo: sendToAll,
  isUsed: false
};

export const requiredSurveyQuestion: ISurveyQuestion = {
  order: 0,
  sendTo: sendToAll,
  question: requiredQuestion
};

export const fromISurveyQToIPreviewQ = ({ question, order }: ISurveyQuestion): IPreviewQuestion => ({
  questionId: question.id,
  text: question.text,
  type: question.type,
  lead: question.lead,
  order,
  options: question.options || [],
  option: question.extraQuestion?.option,
  extraReferenceQuestion: {
    id: question.extraQuestion?.id as string,
    text: question.extraQuestion?.text as string
  }
});

export const removeRequiredQuestion = (selectedQuestions: ISurveyQuestion[]) => filter(
  ({ question }) => !question.isRequired,
  selectedQuestions
);

export const placeholders = {
  name: {
    id: 'surveyBuilder.namePlaceholder',
    defaultMessage: 'Untitled Survey'
  },
  description: {
    id: 'surveyBuilder.descriptionPlaceholder',
    defaultMessage: 'Enter survey description here'
  }
};

export interface IQuestionBlockData {
  icon: JSX.Element;
  title: IFormattedMessage;
  description: IFormattedMessage;
  tip?: IFormattedMessage;
}

export const getQuestionBlockData = (type: QuestionSection): IQuestionBlockData => {
  switch (type) {
    case QuestionSection.Questions:
      return {
        icon: <Icon icon="messageBubbleFirst" />,
        title: {
          id: 'questions.title',
          defaultMessage: 'Customize questions sent to candidates’ references'
        },
        description: {
          id: 'questions.description',
          defaultMessage: `References will answer these free response and yes/no questions. 
          View the question bank to select more or create custom questions.`
        }
      };
    case QuestionSection.SoftSkills:
      return {
        icon: <Icon icon="inactiveStar" />,
        title: {
          id: 'ratingScale.title',
          defaultMessage: 'Customize soft skills measured'
        },
        tip: {
          id: 'ratingScale.tip',
          defaultMessage: 'Question order will be randomized'
        },
        description: {
          id: 'ratingScale.description',
          defaultMessage: `Behavioral questions that measure various competencies.
           Answers to these questions determine the candidate's scores.`
        }
      };
    default:
      return {} as IQuestionBlockData;
  }
};

export const getSelected = (
  questions: IQuestionWithOwner[],
  selectedIds: string[]
): IQuestionWithOwner[] => pipe(
  map(id => find(question => question.id === id, questions) as IQuestionWithOwner),
  filter(question => !isNil(question))
)(selectedIds);

export const getDefaultSelected = (
  isRatingScale: boolean,
  questions: IQuestionWithOwner[],
  selected: ISurveyQuestion[]
): string[] => (
  pipe<
    ISurveyQuestion[],
    ISurveyQuestion[],
    string[]
  >(
    filter(({ question }: ISurveyQuestion) => (isRatingScale
      ? question.type === QuestionType.RatingScale
      : question.type !== QuestionType.RatingScale)),
    map(({ question }) => {
      const item = find(({ id }) => id === question.id, questions);
      return item?.id as string;
    })
  )(selected)
);

export const fromIQuestionToISurveyQuestion = (
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  { createdByUser, isUsed, defaultSendTo, ...question }: IQuestionWithOwner
): ISurveyQuestion => ({
  question,
  order: undefined,
  sendTo: question.type !== QuestionType.RatingScale
    ? (defaultSendTo || sendToAll)
    : undefined
});

export const getUpdatedData = (
  isRatingScale: boolean,
  newQuestions: IQuestionWithOwner[],
  selected: ISurveyQuestion[]
): ISurveyQuestion[] => {
  const [ratingScale, others] = partition(
    ({ question }) => question.type === QuestionType.RatingScale,
    selected
  );
  const updating = isRatingScale ? ratingScale : others;
  const updatedQuestions = map(question => (
    find(item => item.question.id === question.id, updating) || fromIQuestionToISurveyQuestion(question)
  ), newQuestions);

  if (!isRatingScale) {
    return pipe<ISurveyQuestion[], ISurveyQuestion[], ISurveyQuestion[], ISurveyQuestion[]>(
      sort(ascend(prop<string>('order'))),
      addIndex<ISurveyQuestion>(map)((item, order) => ({ ...item, order })),
      concat(ratingScale)
    )(updatedQuestions);
  }

  return concat(updatedQuestions, others);
};
